import { useContext, useState, useEffect, useRef, useMemo } from "react"
import _ from "lodash"
import { useQuery, useLazyQuery } from "@apollo/client"
import { Formik, Form } from "formik"
import * as Yup from "yup"
import { useLocation } from "react-router-dom"
import { TRIM_TYPES, trimTextValue } from "../../../components/FieldTypes/helpers/trimTextValue"

// Prime
import { MultiSelect } from "primereact/multiselect"
import { InputText as Input } from "primereact/inputtext"
import { Button } from "primereact/button"
// Context
import { UserContext } from "../../../context/userContext"

// Comps
import AdvancedSearchResults from "./AdvancedSearchResults"

// Styles
import styles from "../../../styles/Participant-Advaced-Search.module.css"

import {
  PROGRAM_ID_LIST,
  ADVANCED_SEARCH,
  GET_LOCATIONS,
  GET_STAFFS_BY_ORG,
} from "../queries"

const specialStaffMembers = [
  {
    value: "-4",
    label: "Unassigned Awaiting",
  },
  {
    value: "-2",
    label: "Unassigned Discharged",
  },
  {
    value: "-3",
    label: "Unassigned Kiosk Member",
  },
  {
    value: "-1",
    label: "Unassigned Referrals",
  },
]

export default function AdvancedSearch({ searchType }) {
  const userCtx = useContext(UserContext)
  const location = useLocation()
  const hasAutoAppliedPresetRef = useRef(false)
  const [fetching, setFetching] = useState(false)
  const [results, setResults] = useState(null)
  const [programIdList, setProgramIdList] = useState(null)
  const [locationsListByOrg, setLocationsListByOrg] = useState(null)
  const [staffsByOrg, setStaffsByOrg] = useState(null)
  const [isExpanded, setIsExpanded] = useState(true)

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  const { loading } = useQuery(PROGRAM_ID_LIST, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    skip: !userCtx,
    onCompleted: (values) => {
      if (!values) return
      return setProgramIdList(
        _.map(values.programs, (program) => {
          return {
            label: program.program_id_name,
            value: program.program_identity_id,
          }
        })
      )
    },
  })

  const { loading: loadingLocations } = useQuery(GET_LOCATIONS, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    onCompleted: ({ locationsByOrganization }) => {
      return setLocationsListByOrg(
        _.map(locationsByOrganization, (program) => {
          return {
            label: program.location_name,
            value: program.location_id,
          }
        })
      )
    },
  })

  useQuery(GET_STAFFS_BY_ORG, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
    onCompleted: ({ staffsByOrganization }) => {
      const staffs = _.map(staffsByOrganization, (staff) => {
        return {
          label: staff.staff_name_first + " " + staff.staff_name_last,
          value: staff.staff_id,
        }
      })
      const allStaffs = _.concat(staffs, specialStaffMembers)
      setStaffsByOrg(allStaffs)
    },
  })

  const [search, { loading: searching }] = useLazyQuery(ADVANCED_SEARCH, {
    onCompleted: (values) => {
      setResults(values.search)
      return setFetching(false)
    },
  })

  useEffect(() => {}, [])
  const ParticipantSchema = Yup.object().shape({
    participant_full_name: Yup.string(),
    participant_alias: Yup.string(),
    participant_phone_primary: Yup.string(),
    participant_email_account: Yup.string().email("Invalid email"),
    location_name: Yup.string(),
  })

  const statusOptions = [
    { label: "Active Engaged", value: "Active Engaged" },
    { label: "Active Unengaged", value: "Active Unengaged" },
    { label: "Inactive Discharged", value: "Inactive Discharged" },
    { label: "Inactive Referrals", value: "Inactive Referrals" },
    { label: "Kiosk Member", value: "Kiosk Member" },
  ]

  // If navigation passed preset staff IDs, use them to seed Assigned Staff in All Participants.
  const initialStaffIdsFromRoute = useMemo(() => {
    if (searchType !== "all_participants") return []
    if (!Array.isArray(location.state?.presetStaffIds)) return []

    return location.state.presetStaffIds.filter(Boolean).map(String)
  }, [location.state?.presetStaffIds, searchType])

  // Auto-run once on first load when preset staff IDs are present.
  // This keeps normal page behavior unchanged when no preset route state is provided.
  useEffect(() => {
    if (hasAutoAppliedPresetRef.current) return
    if (searchType !== "all_participants") return
    if (initialStaffIdsFromRoute.length === 0) return

    hasAutoAppliedPresetRef.current = true
    setFetching(true)
    search({
      variables: {
        staff_id: userCtx.session.user.staff_id,
        organization_id: userCtx.session.user.organization_id,
        participant_full_name: "",
        participant_name_first: "",
        participant_name_last: "",
        participant_alias: "",
        participant_phone_primary: "",
        participant_email_account: "",
        participant_membership_status: [],
        program_ids: [],
        staff_ids: initialStaffIdsFromRoute,
        locations_ids: [],
        search_type: searchType,
      },
    })
  }, [initialStaffIdsFromRoute, search, searchType, userCtx.session.user.organization_id, userCtx.session.user.staff_id])

  const AdvancedForm = ({ formik }) => {
    return (
      <div className={styles.fields}>
        <div>
          <label>Participant Name:</label>
          <Input
            name="participant_full_name"
            onChange={(e) => formik.handleChange(trimTextValue(e))}
            onBlur={formik.handleBlur}
            value={formik.values.participant_full_name}
          />
        </div>
        <div>
          <label>Participant ID:</label>
          <Input
            name="participant_alias"
            onChange={(e) => formik.handleChange(trimTextValue(e))}
            onBlur={formik.handleBlur}
            value={formik.values.participant_alias}
          />
        </div>
        {!loadingLocations && locationsListByOrg?.length > 0 && (
          <div>
            <label>Location:</label>
            <MultiSelect
              placeholder="Please select a location"
              id="locations_ids"
              name="locations_ids"
              value={formik.values.locations_ids}
              onChange={formik.handleChange}
              options={locationsListByOrg}
            />
          </div>
        )}
        {programIdList && programIdList.length > 0 && (
          <div>
            <label>Program ID:</label>
            <MultiSelect
              placeholder="Please select"
              id="program_ids"
              name="program_ids"
              value={formik.values.program_ids}
              onChange={formik.handleChange}
              options={programIdList}
            />
          </div>
        )}
        {staffsByOrg && staffsByOrg.length > 0 && (
          <div>
            <label>Assigned Staff:</label>
            <MultiSelect
              placeholder="Please select"
              id="staff_ids"
              name="staff_ids"
              value={formik.values.staff_ids}
              onChange={formik.handleChange}
              options={staffsByOrg}
            />
          </div>
        )}
        <div>
          <label>Status:</label>
          <MultiSelect
            placeholder="Please select"
            id="participant_membership_status"
            name="participant_membership_status"
            value={formik.values.participant_membership_status}
            onChange={formik.handleChange}
            options={statusOptions}
          />
        </div>
        <div>
          <label>Phone:</label>
          <Input
            name="participant_phone_primary"
            onChange={(e) => formik.handleChange(trimTextValue(e))}
            onBlur={formik.handleBlur}
            value={formik.values.participant_phone_primary}
          />
        </div>
        <div>
          <label>Email:</label>
          <Input
            name="participant_email_account"
            onChange={(e) => formik.handleChange(trimTextValue(e, TRIM_TYPES.BOTH))}
            onBlur={formik.handleBlur}
            value={formik.values.participant_email_account}
          />
          {formik.touched.participant_email_account &&
            formik.errors.participant_email_account && (
              <div className={styles.errFieldMsg}>
                {formik.errors.participant_email_account}
              </div>
            )}
        </div>
      </div>
    )
  }

  if (loading) return null

  return (
    <div className={styles.wrapper}>
      <h3 className={styles["filter-label"]}>
        {isExpanded ? "" : "Filter Options"}
      </h3>
      <div className={styles.header}>
        <Button
          icon={isExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
          onClick={toggleExpand}
          className={styles["expand-button"]}
        />
      </div>
      {isExpanded && (
        <Formik
          initialValues={{
            participant_full_name: "",
            participant_alias: "",
            participant_phone_primary: "",
            participant_email_account: "",
            participant_membership_status: [],
            program_ids: [],
            staff_ids: initialStaffIdsFromRoute,
            locations_ids: [],
          }}
          validationSchema={ParticipantSchema}
          onSubmit={async (values) => {
            setFetching(true)
            search({
              variables: {
                staff_id: userCtx.session.user.staff_id,
                organization_id: userCtx.session.user.organization_id,
                participant_full_name: values.participant_full_name,
                participant_name_first: values.participant_name_first,
                participant_name_last: values.participant_name_last,
                participant_alias: values.participant_alias,
                participant_phone_primary: values.participant_phone_primary,
                participant_email_account: values.participant_email_account,
                participant_membership_status:
                  values.participant_membership_status,
                program_ids: values.program_ids,
                staff_ids: values.staff_ids,
                locations_ids: values.locations_ids,
                search_type: searchType,
              },
            })
          }}
        >
          {(formik) => (
            <Form>
              <AdvancedForm formik={formik} />
              <div className={styles.buttons}>
                <button
                  type="submit"
                  disabled={searching}
                  className={styles["search-button"]}
                >
                  Search
                </button>
                <Button
                  className={styles["clear-button"]}
                  icon="pi pi-refresh"
                  type="button"
                  label="Reset Filters"
                  disabled={searching}
                  onClick={() => {
                    formik.resetForm()
                    setResults(null)
                  }}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
      <AdvancedSearchResults
        filteredResults={results}
        fetching={fetching}
        searchType={searchType}
      />
    </div>
  )
}
